import type {
  ColInfo,
  ColInfoWithColumnsMap,
  ConnectionParentInfo,
  DatabaseParentInfo,
  Dict,
  FunctionInfo,
  LoggerInterface,
  MapByDatabaseSchemaTable,
  ParentInfoBase,
  ProcedureInfo,
  SchemaFetchResult,
  SchemaPath,
  SchemaSearchResults,
  SchemaTreeResults,
  TableInfo,
  ViewInfo,
} from '@popsql/types';
import { GROUP_EVENTS_MAP, SCHEMA_GROUPS, serializeError } from '@popsql/types';
import type { TransformCallback } from 'stream';

export const COUNT_SQL = 'count(*)';
export const SCHEMA_TREE_FILTER_QUERY_LENGTH_THRESHOLD = 3;
export const SCHEMA_TREE_FILTER_RESULT_LIMIT = 1000;

export const PARENT_PATHS_DB_FIELD_MAP = {
  connectionId: 'connection_id',
  databaseExternalId: 'database_external_id',
  organizationId: 'organization_id',
  schemaName: 'schema_name',
  tableName: 'table_name',
};

/**
 * Raw row shape of the stored `columns` table, as aggregated into JSON by the
 * nested-columns queries in popsql-node (`json_agg(columns.*)`) and
 * popsql-desktop (`json_object(...)`). Keys are the snake_case column names,
 * which only exist in the database, hence the separate shape from `ColInfo`.
 *
 * The boolean-ish flags are typed `boolean | 0 | 1` because the two backends
 * disagree: postgres stores them as booleans and `json_agg` emits `true` /
 * `false`, while desktop stores them in SQLite `INTEGER` columns, so
 * `json_object` emits `0` / `1`. Normalize via `nestedColumnRowToColInfo`
 * rather than reading these fields directly.
 */
export interface NestedColumnRow {
  comment: null | string;
  data_type: string;
  default_value: unknown;
  is_not_null: boolean | 0 | 1;
  is_primary_key: boolean | 0 | 1 | null;
  is_repeated: boolean | 0 | 1 | null;
  is_unique_key: boolean | 0 | 1 | null;
  name: string;
}

/**
 * SQLite has no boolean type; a stored `0` / `1` (or `true` / `false` from
 * postgres) is normalized to a real boolean. `null` and `undefined` pass
 * through so an unset nullable flag stays unset.
 */
function toBool(value: boolean | 0 | 1): boolean;
function toBool(value: boolean | 0 | 1 | null): boolean | null;
function toBool(value: boolean | 0 | 1 | null): boolean | null {
  return value == null ? value : Boolean(value);
}

/** Maps a stored nested-column row to the `ColInfo` shape used everywhere else. */
export const nestedColumnRowToColInfo = (row: NestedColumnRow): ColInfo => ({
  comment: row.comment,
  dataType: row.data_type,
  default: row.default_value,
  isNotNull: toBool(row.is_not_null),
  isPrimaryKey: toBool(row.is_primary_key),
  isRepeated: toBool(row.is_repeated),
  isUniqueKey: toBool(row.is_unique_key),
  name: row.name,
});

export const GROUPS_UNDER_SCHEMA = [
  SCHEMA_GROUPS.tables,
  SCHEMA_GROUPS.views,
  SCHEMA_GROUPS.routines,
];

export const GROUPS_UNDER_TABLE = [
  SCHEMA_GROUPS.columns,
  SCHEMA_GROUPS.indexes,
  SCHEMA_GROUPS.triggers,
];

export interface DatabaseSchemaPathMaps {
  databaseRequestedGroupsMap: Dict<Set<SCHEMA_GROUPS>>;
  databaseSchemasMap: Dict<Set<string>>;
  databaseTablesMap: Dict<Set<string>>;
  databaseTablesSchemasMap: Dict<Set<string>>;
}

enum SchemaNodeKeyType {
  pinned = 'p',
  table = 't',
  view = 'v',
}
export interface FetchSchemaGroupArgs {
  selectArgs: DbQueryArgs;
  transform: (
    data: Record<string, unknown>,
    encoding: BufferEncoding,
    done: TransformCallback,
  ) => void;
}

export interface DbQueryArgs {
  queryArgs: unknown[];
  sqlString: string;
}

interface GroupSizeArgs {
  notNullColumns: string[];
  sqlAddition: string;
  tableName: string;
}

export const schemaNodeKeyToSchemaPath = (
  stringKey: string,
): SchemaPath | null => {
  // schema node keys follow the format of <database>::<schema>::<t or v or p>:<table>
  const props = stringKey.split(':').filter((v) => v);
  const database = props.length ? props[0] : null;
  const schema = props.length >= 2 ? props[1] : null;
  const table =
    props.length >= 3 &&
    Object.values(SchemaNodeKeyType)
      .map((v) => v.toString())
      .includes(props[2])
      ? props[3]
      : null;
  return database && schema ? { database, schema, table } : null;
};

export const getParentPathFilterArgs = (
  group: SCHEMA_GROUPS,
  selectedSchemas: string[] | null,
  selectedTables: string[] | null,
): Dict<string[]> => {
  const parentPathFilterArgs: Dict<string[]> = {};
  if (
    selectedSchemas &&
    ![SCHEMA_GROUPS.schemas, SCHEMA_GROUPS.default_schema].includes(group)
  ) {
    parentPathFilterArgs.schemaName = selectedSchemas;
  }

  if (selectedTables && GROUPS_UNDER_TABLE.includes(group)) {
    parentPathFilterArgs.tableName = selectedTables;
  }
  return parentPathFilterArgs;
};

export const getGroupSizeArgs = (group: string): GroupSizeArgs | null => {
  let tableName: string;
  let notNullColumns: string[] = [];
  let sqlAddition = '';
  switch (group) {
    case SCHEMA_GROUPS.databases:
      tableName = 'databases';
      break;
    case SCHEMA_GROUPS.default_schema:
      tableName = 'default_schemas';
      break;
    case SCHEMA_GROUPS.schemas:
      tableName = 'schemas';
      break;
    case SCHEMA_GROUPS.tables:
      tableName = 'tables';
      notNullColumns = ['schemaName'];
      break;
    case SCHEMA_GROUPS.views:
      tableName = 'views';
      notNullColumns = ['schemaName'];
      break;
    case SCHEMA_GROUPS.routines:
      tableName = 'routines';
      notNullColumns = ['schemaName'];
      break;
    case SCHEMA_GROUPS.columns:
      tableName = 'columns';
      notNullColumns = ['schemaName', 'tableName'];
      sqlAddition = ' AND t.path is NULL';
      break;
    case SCHEMA_GROUPS.triggers:
      tableName = 'triggers';
      notNullColumns = ['schemaName', 'tableName'];
      break;
    case SCHEMA_GROUPS.indexes:
      tableName = 'indexes';
      notNullColumns = ['schemaName', 'tableName'];
      break;
    default:
      return null;
  }
  return { notNullColumns, sqlAddition, tableName };
};

export const fetchSchemaGroupArgs = async (
  group: SCHEMA_GROUPS,
  parentInfo: ConnectionParentInfo | DatabaseParentInfo,
  selectedSchemas: string[] | null,
  selectedTables: string[] | null,
  logger: LoggerInterface,
  getSelectArgs: (
    fieldsToSelect: string[],
    tableName: string,
    parentInfo: ConnectionParentInfo | DatabaseParentInfo,
    parentPathFilterArgs: Dict<string[]>,
    notNullColumns?: string[],
  ) => Promise<DbQueryArgs>,
  getNestedColumns: (topLevelColumn) => Promise<ColInfo[]>,
): Promise<FetchSchemaGroupArgs> => {
  let transform;
  let selectArgs;
  const parentPathFilterArgs = getParentPathFilterArgs(
    group,
    selectedSchemas,
    selectedTables,
  );
  switch (group) {
    case SCHEMA_GROUPS.databases:
      selectArgs = await getSelectArgs(
        ['t.external_id', 't.name'],
        'databases',
        parentInfo,
        parentPathFilterArgs,
      );
      transform = (row, _, done) => {
        done(null, { id: row.external_id, name: row.name });
      };
      break;
    case SCHEMA_GROUPS.default_schema:
      selectArgs = await getSelectArgs(
        ['t.name'],
        'default_schemas',
        parentInfo,
        parentPathFilterArgs,
      );
      transform = (row, _, done) => done(null, row.name);
      break;
    case SCHEMA_GROUPS.schemas:
      selectArgs = await getSelectArgs(
        ['t.name'],
        'schemas',
        parentInfo,
        parentPathFilterArgs,
      );
      transform = (row, _, done) => done(null, { name: row.name });
      break;
    case SCHEMA_GROUPS.tables:
      selectArgs = await getSelectArgs(
        ['p.schema_name', 't.name', 't.comment'],
        'tables',
        parentInfo,
        parentPathFilterArgs,
        ['schemaName'],
      );
      transform = (row, _, done) => {
        done(null, {
          data: { comment: row.comment, name: row.name },
          schema: row.schema_name,
        });
      };
      break;
    case SCHEMA_GROUPS.views:
      selectArgs = await getSelectArgs(
        ['p.schema_name', 't.name', 't.definition', 't.type', 't.comment'],
        'views',
        parentInfo,
        parentPathFilterArgs,
        ['schemaName'],
      );
      transform = (row, _, done) => {
        done(null, {
          data: {
            definition: row.definition,
            name: row.name,
            type: row.type,
          },
          schema: row.schema_name,
        });
      };
      break;
    case SCHEMA_GROUPS.routines:
      selectArgs = await getSelectArgs(
        ['p.schema_name', 't.name', 't.definition', 't.type'],
        'routines',
        parentInfo,
        parentPathFilterArgs,
        ['schemaName'],
      );
      transform = (row, _, done) => {
        done(null, {
          data: {
            definition: row.definition,
            name: row.name,
            type: row.type,
          },
          schema: row.schema_name,
        });
      };
      break;
    case SCHEMA_GROUPS.columns:
      selectArgs = await getSelectArgs(
        [
          'p.schema_name',
          'p.table_name',
          't.name',
          't.column_order',
          't.data_type',
          't.is_not_null',
          't.is_repeated',
          't.is_primary_key',
          't.is_unique_key',
          't.default_value',
          't.comment',
          't.parent_path_id',
        ],
        'columns',
        parentInfo,
        parentPathFilterArgs,
        ['schemaName', 'tableName'],
      );
      selectArgs.sqlString =
        selectArgs.sqlString +
        ' AND t.path is NULL ORDER BY t.column_order ASC';
      transform = async (row, _, done) => {
        const columns =
          row.data_type === 'RECORD' ? await getNestedColumns(row) : null;
        done(null, {
          data: {
            // Share the mapping with the nested columns, so both levels
            // normalize the boolean-ish flags the same way.
            ...nestedColumnRowToColInfo(row as NestedColumnRow),
            columnOrder: row.column_order,
            columns,
          },
          schema: row.schema_name,
          table: row.table_name,
        });
      };
      break;
    case SCHEMA_GROUPS.triggers:
      selectArgs = await getSelectArgs(
        [
          'p.schema_name',
          'p.table_name',
          't.name',
          't.manipulation',
          't.timing',
          't.statement',
        ],
        'triggers',
        parentInfo,
        parentPathFilterArgs,
        ['schemaName', 'tableName'],
      );
      transform = (row, _, done) => {
        done(null, {
          data: {
            definition: row.definition,
            manipulation: row.manipulation,
            name: row.name,
            statement: row.statement,
            timing: row.timing,
          },
          schema: row.schema_name,
          table: row.table_name,
        });
      };
      break;
    case SCHEMA_GROUPS.indexes:
      selectArgs = await getSelectArgs(
        ['p.schema_name', 'p.table_name', 't.name', 't.definition'],
        'indexes',
        parentInfo,
        parentPathFilterArgs,
        ['schemaName', 'tableName'],
      );
      transform = (row, _, done) => {
        done(null, {
          data: {
            definition: row.definition,
            name: row.name,
          },
          schema: row.schema_name,
          table: row.table_name,
        });
      };
      break;
    default:
      throw new Error(
        `fetchSchemaGroupArgs is not yet implemented for ${group}`,
      );
  }

  if (!selectArgs || !transform) {
    throw new Error(
      `fetchSchemaGroupArgs is missing return values for ${group}`,
    );
  }

  return { selectArgs, transform };
};

export const handleSchemaGroupError = (
  updateFunc: (
    eventName: string,
    results: SchemaFetchResult<
      unknown,
      ConnectionParentInfo | DatabaseParentInfo
    >,
  ) => void,
  msg: string,
  originalError: Error,
  group: string,
  isCloud: boolean,
  database: string,
) => {
  const errorResult = {
    database,
    error: {
      errorMessage: msg,
      originalError: serializeError(originalError),
    },
    group,
    isCloud,
  };
  const eventName = GROUP_EVENTS_MAP[group];
  updateFunc(eventName, errorResult);
};

export const convertSchemaNodeKeysToMaps = (
  database: string,
  schemaNodeKeys: string[],
  fetchAllBigQueryProjectSchemas: boolean,
): DatabaseSchemaPathMaps => {
  // map of database to schema groups that should be fetched under it
  const databaseRequestedGroupsMap: Dict<Set<SCHEMA_GROUPS>> = {};
  // maps of database to sets of schemas and tables that should be fetched under it
  const databaseSchemasMap: Dict<Set<string>> = {};
  const databaseTablesMap: Dict<Set<string>> = {};
  const databaseTablesSchemasMap: Dict<Set<string>> = {};

  // parse the schemaNodeKeys to find requested schema groups,
  // list of schemas, and list of tables for each database
  schemaNodeKeys.forEach((key) => {
    const schemaPath = schemaNodeKeyToSchemaPath(key);
    if (!schemaPath) {
      return;
    }

    const { database: pathDb, schema, table } = schemaPath;
    // only create the maps for requested database or if fetchAllBigQueryProjectSchemas is true
    if (pathDb !== database && !fetchAllBigQueryProjectSchemas) {
      return;
    }

    let databaseRequestGroups = databaseRequestedGroupsMap[pathDb] || new Set();

    if (table) {
      // add the schema and table to list for the database
      const databaseSchemasList = databaseTablesSchemasMap[pathDb] || new Set();
      databaseSchemasList.add(schema);
      databaseTablesSchemasMap[pathDb] = databaseSchemasList;
      const databaseTablesList = databaseTablesMap[pathDb] || new Set();
      databaseTablesList.add(table);
      databaseTablesMap[pathDb] = databaseTablesList;
      databaseRequestGroups = new Set([
        ...databaseRequestGroups,
        ...GROUPS_UNDER_TABLE,
      ]);
    } else {
      const databaseSchemasList = databaseSchemasMap[pathDb] || new Set();
      databaseSchemasList.add(schema);
      databaseSchemasMap[pathDb] = databaseSchemasList;
      databaseRequestGroups = new Set([
        ...databaseRequestGroups,
        ...GROUPS_UNDER_SCHEMA,
      ]);
    }
    databaseRequestedGroupsMap[pathDb] = databaseRequestGroups;
  });

  return {
    databaseRequestedGroupsMap,
    databaseSchemasMap,
    databaseTablesMap,
    databaseTablesSchemasMap,
  };
};

export const createSchemaNodeKey = (
  database: string,
  schema: string,
  name: string,
  isTable: boolean,
): string => {
  const type = isTable ? 't' : 'v';
  return `${database}::${schema}::${type}:${name}`;
};

export const convertSchemaTreeResults = (
  start: number,
  rows: SchemaSearchResults,
  logger: LoggerInterface,
): SchemaTreeResults => {
  const dedupKeySet = new Set<string>();
  const results: SchemaTreeResults = {
    columns: {},
    functions: {},
    indexes: {},
    latency: Date.now() - start,
    procedures: {},
    schemaNodePaths: [],
    tables: {},
    triggers: {},
    views: {},
  };

  const databaseSchemaTablesMap: Dict<Dict<Dict<TableInfo>>> = {};
  const databaseSchemaViewsMap: Dict<Dict<Dict<ViewInfo>>> = {};

  // map of database -> schema -> table -> toplevel column name -> ColInfo with map of child columns
  // this is transformed at the end of result mapping to update the `columns` field of the toplevel columns
  const nestedColumns: MapByDatabaseSchemaTable<Dict<ColInfoWithColumnsMap>> =
    {};

  rows.results.forEach((row) => {
    const dedupKey = JSON.stringify([
      row.database,
      row.schema,
      row.table,
      row.type,
      row.name,
      row.path,
    ]);
    if (dedupKeySet.has(dedupKey)) {
      // critical that we avoid any duplicate keys in the tree
      logger.error('Received duplicate schema search result', row);
      return;
    }
    dedupKeySet.add(dedupKey);
    // All column results should have the 'table' and 'dataType' fields populated
    if (row.type === 'column' && row.table && row.dataType) {
      const col: ColInfo = {
        comment: row.comment,
        dataType: row.dataType,
        default: row.defaultValue,
        isNotNull: !!row.isNotNull,
        isPrimaryKey: row.isPrimaryKey,
        isRepeated: row.isRepeated === null ? undefined : row.isRepeated,
        isUniqueKey: row.isUniqueKey,
        name: row.name,
      };

      if (row.path) {
        // a lot of intermediate variables here due to ts2279 and TS not recognizing
        // we already handled ensuring existence of keys, maybe there's a better way
        const topLevelColName = row.path.split('.')[0];
        const nestedColsSchemas = nestedColumns[row.database] ?? {};
        const nestedColsSchemaTables = nestedColsSchemas[row.schema] ?? {};
        const nestedColsTableTopLevel = nestedColsSchemaTables[row.table] ?? {};
        const topLevelCol = nestedColsTableTopLevel[topLevelColName] ?? {
          columnsMap: {},
        };
        let lastParentCol = topLevelCol;
        for (const parentCol of [...row.path.split('.').slice(1), row.name]) {
          const currentParentCol = lastParentCol.columnsMap[parentCol] ?? {
            columnsMap: {},
          };
          lastParentCol.columnsMap[parentCol] = {
            colInfo: parentCol === row.name ? col : currentParentCol.colInfo,
            columnsMap: currentParentCol.columnsMap,
          };
          lastParentCol = currentParentCol;
        }
        nestedColsTableTopLevel[topLevelColName] = topLevelCol;
        nestedColsSchemaTables[row.table] = nestedColsTableTopLevel;
        nestedColsSchemas[row.schema] = nestedColsSchemaTables;
        nestedColumns[row.database] = nestedColsSchemas;
      } else {
        addToMapByDatabaseSchemaTableList(
          results.columns,
          row.database,
          row.schema,
          row.table,
          col,
        );
        addToMapByDatabaseSchemaTable(
          databaseSchemaTablesMap,
          row.database,
          row.schema,
          row.table,
          {
            name: row.table,
          },
        );
      }
      // All trigger results should have the 'table' field populated
    } else if (row.type === 'trigger' && row.table) {
      addToMapByDatabaseSchemaTableList(
        results.triggers,
        row.database,
        row.schema,
        row.table,
        {
          manipulation:
            row.manipulation === null ? undefined : row.manipulation,
          name: row.name,
          statement: row.statement === null ? undefined : row.statement,
          timing: row.timing === null ? undefined : row.timing,
        },
      );
      addToMapByDatabaseSchemaTable(
        databaseSchemaTablesMap,
        row.database,
        row.schema,
        row.table,
        {
          name: row.table,
        },
      );
      // All index results should have the 'table' and 'definition' fields populated
    } else if (row.type === 'index' && row.table && row.definition) {
      addToMapByDatabaseSchemaTableList(
        results.indexes,
        row.database,
        row.schema,
        row.table,
        {
          definition: row.definition,
          name: row.name,
        },
      );
      addToMapByDatabaseSchemaTable(
        databaseSchemaTablesMap,
        row.database,
        row.schema,
        row.table,
        {
          name: row.table,
        },
      );
    } else if (row.type === 'table') {
      addToMapByDatabaseSchemaTable(
        databaseSchemaTablesMap,
        row.database,
        row.schema,
        row.name,
        {
          comment: row.comment,
          name: row.name,
        },
      );
      results.schemaNodePaths.push(
        createSchemaNodeKey(row.database, row.schema, row.name, true),
      );
      addToMapByDatabaseSchemaTableList(
        results.columns,
        row.database,
        row.schema,
        row.name,
      );
      addToMapByDatabaseSchemaTableList(
        results.indexes,
        row.database,
        row.schema,
        row.name,
      );
      addToMapByDatabaseSchemaTableList(
        results.triggers,
        row.database,
        row.schema,
        row.name,
      );
    } else if (row.type === 'view') {
      addToMapByDatabaseSchemaTable(
        databaseSchemaViewsMap,
        row.database,
        row.schema,
        row.name,
        {
          comment: row.comment,
          definition: row.definition === null ? undefined : row.definition,
          name: row.name,
          type: row.type,
        },
      );
      results.schemaNodePaths.push(
        createSchemaNodeKey(row.database, row.schema, row.name, false),
      );
      addToMapByDatabaseSchemaTableList(
        results.columns,
        row.database,
        row.schema,
        row.name,
      );
      // All routine results should have the 'routineType' field populated
    } else if (
      row.type === 'routine' &&
      row.routineType &&
      ['PROCEDURE', 'FUNCTION'].includes(row.routineType)
    ) {
      addToMapByDatabaseSchemaTable<ProcedureInfo | FunctionInfo>(
        row.routineType === 'PROCEDURE'
          ? results.procedures
          : results.functions,
        row.database,
        row.schema,
        row.name,
        {
          definition: row.definition,
          name: row.name,
          type: row.routineType === 'PROCEDURE' ? 'PROCEDURE' : 'FUNCTION',
        },
      );
    } else if (row.type === 'schema') {
      results.schemaNodePaths.push(`${row.database}::${row.name}`);
    }
  });

  // iterate through the nested columns and update toplevel column's `columns` field
  Object.entries(nestedColumns).forEach(([database, schemasTablesMap]) => {
    Object.entries(schemasTablesMap ?? {}).forEach(
      ([schema, tablesTopLevelMap]) => {
        Object.entries(tablesTopLevelMap ?? {}).forEach(
          ([table, topLevelColsMap]) => {
            Object.entries(topLevelColsMap ?? {}).forEach(
              ([topLevelColName, topLevelColColumnsMap]) => {
                const topLevelCol = results.columns[database]?.[schema]?.[
                  table
                ]?.find((c) => c.name === topLevelColName);
                if (topLevelCol) {
                  topLevelCol.columns = convertColumnsMapToColumnsArray(
                    topLevelColColumnsMap?.columnsMap ?? {},
                  );
                }
              },
            );
          },
        );
      },
    );
  });

  results.tables = Object.fromEntries(
    Object.entries(databaseSchemaTablesMap).map(([database, schemaMap]) => {
      return [
        database,
        Object.fromEntries(
          Object.entries(schemaMap ?? {}).map(([schema, tableMap]) => {
            return [schema, Object.values(tableMap ?? {}) as TableInfo[]];
          }),
        ),
      ];
    }),
  );
  results.views = Object.fromEntries(
    Object.entries(databaseSchemaViewsMap).map(([database, schemaMap]) => {
      return [
        database,
        Object.fromEntries(
          Object.entries(schemaMap ?? {}).map(([schema, viewMap]) => {
            return [schema, Object.values(viewMap ?? {}) as ViewInfo[]];
          }),
        ),
      ];
    }),
  );
  return results;
};

const addToMapByDatabaseSchemaTableList = <T>(
  mapByDatabaseSchemaTable: MapByDatabaseSchemaTable<T[]>,
  database: string,
  schema: string,
  table: string,
  item?: T,
) => {
  const dbSchemaTableMap = mapByDatabaseSchemaTable[database] ?? {};
  const tableMap = dbSchemaTableMap[schema] ?? {};
  tableMap[table] = tableMap[table] ?? [];
  if (item) {
    tableMap[table]?.push(item);
  }
  dbSchemaTableMap[schema] = tableMap;
  mapByDatabaseSchemaTable[database] = dbSchemaTableMap;
};

const addToMapByDatabaseSchemaTable = <T>(
  mapByDatabaseSchemaTable: MapByDatabaseSchemaTable<T>,
  database: string,
  schema: string,
  table: string,
  item: T,
) => {
  const dbSchemaTableMap = mapByDatabaseSchemaTable[database] ?? {};
  const tableMap = dbSchemaTableMap[schema] ?? {};
  tableMap[table] = item;
  dbSchemaTableMap[schema] = tableMap;
  mapByDatabaseSchemaTable[database] = dbSchemaTableMap;
};

const convertColumnsMapToColumnsArray = (
  columnsMap: Dict<ColInfoWithColumnsMap>,
): ColInfo[] => {
  const res: ColInfo[] = [];
  Object.values(columnsMap).forEach((colInfoWithColumnsMap) => {
    if (colInfoWithColumnsMap && colInfoWithColumnsMap.colInfo) {
      const colInfo: ColInfo = {
        ...colInfoWithColumnsMap?.colInfo,
        columns: convertColumnsMapToColumnsArray(
          colInfoWithColumnsMap?.columnsMap ?? {},
        ),
      };
      res.push(colInfo);
    }
  });
  return res;
};

// NOTE: The query generated by this function is used by both popsql-node and
// popsql-desktop. The SQL generated must therefore be valid for both Postgres
// and SQLite.
export const getParentPathArgs = ({
  columnNames = [],
  parentInfo,
  parentPathFilterArgs = {},
  schema,
  tableRef = '',
  whereClauseOnly = false,
}: {
  columnNames?: string[];
  parentInfo: ParentInfoBase;
  parentPathFilterArgs?: Dict<string[]>;
  schema?: string;
  tableRef?: string;
  whereClauseOnly?: boolean;
}): { queryArgs: string[]; sqlString: string } => {
  let argIndex = 0;
  const queryArgs: string[] = [];
  const whereClause: string[] = Object.keys(PARENT_PATHS_DB_FIELD_MAP).map(
    (key) => {
      if (Object.keys(parentPathFilterArgs).includes(key)) {
        return `${tableRef}${PARENT_PATHS_DB_FIELD_MAP[key]} IN (${(
          parentPathFilterArgs[key] ?? []
        )
          .map((s) => {
            queryArgs.push(s);
            argIndex += 1;
            return `$${argIndex}`;
          })
          .join(',')})`;
      }

      if (columnNames.includes(key)) {
        return `${tableRef}${PARENT_PATHS_DB_FIELD_MAP[key]} IS NOT NULL`;
      }

      if (parentInfo[key]) {
        queryArgs.push(parentInfo[key]);
        argIndex += 1;
      }
      const query = parentInfo[key] ? `= $${argIndex}` : 'is NULL';
      return `${tableRef}${PARENT_PATHS_DB_FIELD_MAP[key]} ${query}`;
    },
  );

  const whereClauseSql = `WHERE ${whereClause.join(' AND ')}`;
  return {
    queryArgs,
    sqlString: whereClauseOnly
      ? whereClauseSql
      : `SELECT id from ${
          schema ? `${schema}.` : ''
        }parent_paths ${whereClauseSql} ORDER BY id`,
  };
};
